import * as React from "react";
import { Route, Switch, Redirect, useLocation } from "wouter";
import { AuthLayout } from "./layout/AuthLayout";
import { LoginPage } from "./components/auth/LoginPage";
import { ForgotPasswordPage } from "./components/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "./components/auth/ResetPasswordPage";
import { DashboardLayout } from "./components/dashboard/DashboardLayout";
import { OnboardingWizard } from "./components/onboarding/OnboardingWizard";
import { CallsignLookupPage } from "./components/common/CallsignLookupPage";
import { ProtectedRoute } from "./components/routing/ProtectedRoute";
import { PublicRoute } from "./components/routing/PublicRoute";
import { useAuth } from "./contexts/AuthContext";
import { hasAssignmentPrivilege } from "./utils/privileges";
import "./index.css";

// Lazy load dashboard pages
const DashboardPage = React.lazy(() => import("./components/dashboard/DashboardPage").then(m => ({ default: m.DashboardPage })));
const UserDetailPage = React.lazy(() => import("./components/dashboard/UserDetailPage").then(m => ({ default: m.UserDetailPage })));
const UserDetailByIdPage = React.lazy(() => import("./components/dashboard/UserDetailByIdPage").then(m => ({ default: m.UserDetailByIdPage })));
const HistoricalTransactionPage = React.lazy(() => import("./components/dashboard/HistoricalTransactionPage").then(m => ({ default: m.HistoricalTransactionPage })));
const KeanggotaanPage = React.lazy(() => import("./components/dashboard/KeanggotaanPage").then(m => ({ default: m.KeanggotaanPage })));
const FotoKtpPage = React.lazy(() => import("./components/dashboard/FotoKtpPage").then(m => ({ default: m.FotoKtpPage })));
const SilentKeyPage = React.lazy(() => import("./components/dashboard/SilentKeyPage").then(m => ({ default: m.SilentKeyPage })));
const NriRequestsPage = React.lazy(() => import("./components/dashboard/NriRequestsPage").then(m => ({ default: m.NriRequestsPage })));
const LogbookPage = React.lazy(() => import("./components/dashboard/LogbookPage").then(m => ({ default: m.LogbookPage })));
const LogbookEntryFormPage = React.lazy(() => import("./components/dashboard/LogbookEntryFormPage").then(m => ({ default: m.LogbookEntryFormPage })));
const QSLSyncPage = React.lazy(() => import("./components/dashboard/QSLSyncPage").then(m => ({ default: m.QSLSyncPage })));
const BlockPage = React.lazy(() => import("./components/dashboard/BlockPage").then(m => ({ default: m.BlockPage })));
const ManagementPage = React.lazy(() => import("./components/dashboard/ManagementPage").then(m => ({ default: m.ManagementPage })));
const AdminManagementPage = React.lazy(() => import("./components/dashboard/AdminManagementPage").then(m => ({ default: m.AdminManagementPage })));
const RekeningPage = React.lazy(() => import("./components/dashboard/RekeningPage").then(m => ({ default: m.RekeningPage })));
const TransferPlanPage = React.lazy(() => import("./components/dashboard/TransferPlanPage").then(m => ({ default: m.TransferPlanPage })));
const TransferPlanListPage = React.lazy(() => import("./components/dashboard/TransferPlanListPage").then(m => ({ default: m.TransferPlanListPage })));
const TransferPlanDetailPage = React.lazy(() => import("./components/dashboard/TransferPlanDetailPage").then(m => ({ default: m.TransferPlanDetailPage })));
const TransferPlanLocationPage = React.lazy(() => import("./components/dashboard/TransferPlanLocationPage").then(m => ({ default: m.TransferPlanLocationPage })));
const UserSyncPage = React.lazy(() => import("./components/dashboard/UserSyncPage").then(m => ({ default: m.UserSyncPage })));

// Loading fallback component
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

// Login page wrapper
function LoginPageWrapper() {
  return (
    <AuthLayout>
      <LoginPage />
    </AuthLayout>
  );
}

// Forgot password page wrapper
function ForgotPasswordPageWrapper() {
  return (
    <AuthLayout>
      <ForgotPasswordPage />
    </AuthLayout>
  );
}

// Reset password page wrapper
function ResetPasswordPageWrapper() {
  return (
    <AuthLayout>
      <ResetPasswordPage />
    </AuthLayout>
  );
}

// Dashboard page wrapper
function DashboardPageWrapper() {
  const { user, refreshUser } = useAuth();
  const [, setLocation] = useLocation();

  // Redirect to /user if user cannot access dashboard (view_reports on an active assignment only).
  if (user && !hasAssignmentPrivilege(user, "view_reports")) {
    setLocation("/user");
    return null;
  }

  // Check if user needs onboarding - show full screen without layout
  if (user && (user.isPasswordSet === false || user.isSetCompleteAddress === false || !user.isUpdatedEmail || !user.isUpdatePhotos)) {
    return (
      <OnboardingWizard
        user={user}
        onComplete={async () => {
          await refreshUser();
          // Wizard will show dashboard after refreshUser updates the user state
        }}
      />
    );
  }

  return (
    <DashboardLayout>
      <React.Suspense fallback={<PageLoader />}>
        <DashboardPage />
      </React.Suspense>
    </DashboardLayout>
  );
}

// User detail page wrapper
function UserDetailPageWrapper() {
  const { user, refreshUser } = useAuth();

  // Check if user needs onboarding - show full screen without layout
  if (user && (user.isPasswordSet === false || user.isSetCompleteAddress === false)) {
    return (
      <OnboardingWizard
        user={user}
        onComplete={async () => {
          await refreshUser();
        }}
      />
    );
  }

  // Show with layout (navbar and sidebar) for all user levels
  return (
    <DashboardLayout>
      <React.Suspense fallback={<PageLoader />}>
        <UserDetailPage />
      </React.Suspense>
    </DashboardLayout>
  );
}

// Historical transaction page wrapper
function HistoricalTransactionPageWrapper() {
  const { user, refreshUser } = useAuth();

  // Check if user needs onboarding - show full screen without layout
  if (user && (user.isPasswordSet === false || user.isSetCompleteAddress === false)) {
    return (
      <OnboardingWizard
        user={user}
        onComplete={async () => {
          await refreshUser();
        }}
      />
    );
  }

  // Show with layout (navbar and sidebar)
  return (
    <DashboardLayout>
      <React.Suspense fallback={<PageLoader />}>
        <HistoricalTransactionPage />
      </React.Suspense>
    </DashboardLayout>
  );
}

// Keanggotaan page wrapper
function KeanggotaanPageWrapper() {
  const { user, refreshUser } = useAuth();

  // Check if user needs onboarding - show full screen without layout
  if (user && (user.isPasswordSet === false || user.isSetCompleteAddress === false)) {
    return (
      <OnboardingWizard
        user={user}
        onComplete={async () => {
          await refreshUser();
        }}
      />
    );
  }

  // Show with layout (navbar and sidebar)
  return (
    <DashboardLayout>
      <React.Suspense fallback={<PageLoader />}>
        <KeanggotaanPage />
      </React.Suspense>
    </DashboardLayout>
  );
}

// Foto KTP page wrapper
function FotoKtpPageWrapper() {
  const { user, refreshUser } = useAuth();

  // Check if user needs onboarding - show full screen without layout
  if (user && (user.isPasswordSet === false || user.isSetCompleteAddress === false)) {
    return (
      <OnboardingWizard
        user={user}
        onComplete={async () => {
          await refreshUser();
        }}
      />
    );
  }

  // Show with layout (navbar and sidebar)
  return (
    <DashboardLayout>
      <React.Suspense fallback={<PageLoader />}>
        <FotoKtpPage />
      </React.Suspense>
    </DashboardLayout>
  );
}

function SilentKeyPageWrapper() {
  const { user, refreshUser } = useAuth();

  if (user && (user.isPasswordSet === false || user.isSetCompleteAddress === false)) {
    return (
      <OnboardingWizard
        user={user}
        onComplete={async () => {
          await refreshUser();
        }}
      />
    );
  }

  return (
    <DashboardLayout>
      <React.Suspense fallback={<PageLoader />}>
        <SilentKeyPage />
      </React.Suspense>
    </DashboardLayout>
  );
}

function NriRequestsPageWrapper() {
  const { user, refreshUser } = useAuth();

  if (user && (user.isPasswordSet === false || user.isSetCompleteAddress === false)) {
    return (
      <OnboardingWizard
        user={user}
        onComplete={async () => {
          await refreshUser();
        }}
      />
    );
  }

  return (
    <DashboardLayout>
      <React.Suspense fallback={<PageLoader />}>
        <NriRequestsPage />
      </React.Suspense>
    </DashboardLayout>
  );
}

// Logbook entry form page wrapper
function LogbookEntryFormPageWrapper() {
  const { user, refreshUser } = useAuth();

  // Check if user needs onboarding - show full screen without layout
  if (user && (user.isPasswordSet === false || user.isSetCompleteAddress === false)) {
    return (
      <OnboardingWizard
        user={user}
        onComplete={async () => {
          await refreshUser();
        }}
      />
    );
  }

  // Show with layout (navbar and sidebar)
  return (
    <DashboardLayout>
      <React.Suspense fallback={<PageLoader />}>
        <LogbookEntryFormPage />
      </React.Suspense>
    </DashboardLayout>
  );
}

// Logbook page wrapper
function LogbookPageWrapper() {
  const { user, refreshUser } = useAuth();

  // Check if user needs onboarding - show full screen without layout
  if (user && (user.isPasswordSet === false || user.isSetCompleteAddress === false)) {
    return (
      <OnboardingWizard
        user={user}
        onComplete={async () => {
          await refreshUser();
        }}
      />
    );
  }

  // Show with layout (navbar and sidebar)
  return (
    <DashboardLayout>
      <React.Suspense fallback={<PageLoader />}>
        <LogbookPage />
      </React.Suspense>
    </DashboardLayout>
  );
}

// QSL Sync page wrapper
function QSLSyncPageWrapper() {
  const { user, refreshUser } = useAuth();

  // Check if user needs onboarding - show full screen without layout
  if (user && (user.isPasswordSet === false || user.isSetCompleteAddress === false)) {
    return (
      <OnboardingWizard
        user={user}
        onComplete={async () => {
          await refreshUser();
        }}
      />
    );
  }

  // Show with layout (navbar and sidebar)
  return (
    <DashboardLayout>
      <React.Suspense fallback={<PageLoader />}>
        <QSLSyncPage />
      </React.Suspense>
    </DashboardLayout>
  );
}

function InboxRedirectWrapper() {
  return <Redirect href="/user" />;
}

// Block page wrapper
function BlockPageWrapper() {
  const { user, refreshUser } = useAuth();

  // Check if user needs onboarding - show full screen without layout
  if (user && (user.isPasswordSet === false || user.isSetCompleteAddress === false)) {
    return (
      <OnboardingWizard
        user={user}
        onComplete={async () => {
          await refreshUser();
        }}
      />
    );
  }

  // Show with layout (navbar and sidebar)
  return (
    <DashboardLayout>
      <React.Suspense fallback={<PageLoader />}>
        <BlockPage />
      </React.Suspense>
    </DashboardLayout>
  );
}

// UserSync page wrapper
function UserSyncPageWrapper() {
  const { user, refreshUser } = useAuth();

  if (user && (user.isPasswordSet === false || user.isSetCompleteAddress === false)) {
    return (
      <OnboardingWizard
        user={user}
        onComplete={async () => {
          await refreshUser();
        }}
      />
    );
  }

  return (
    <DashboardLayout>
      <React.Suspense fallback={<PageLoader />}>
        <UserSyncPage />
      </React.Suspense>
    </DashboardLayout>
  );
}

// Management page wrapper
function ManagementPageWrapper() {
  const { user, refreshUser } = useAuth();

  // Check if user needs onboarding - show full screen without layout
  if (user && (user.isPasswordSet === false || user.isSetCompleteAddress === false)) {
    return (
      <OnboardingWizard
        user={user}
        onComplete={async () => {
          await refreshUser();
        }}
      />
    );
  }

  // Show with layout (navbar and sidebar)
  return (
    <DashboardLayout>
      <React.Suspense fallback={<PageLoader />}>
        <ManagementPage />
      </React.Suspense>
    </DashboardLayout>
  );
}

// Admin management page wrapper
function AdminManagementPageWrapper() {
  const { user, refreshUser } = useAuth();

  // Check if user needs onboarding - show full screen without layout
  if (user && (user.isPasswordSet === false || user.isSetCompleteAddress === false)) {
    return (
      <OnboardingWizard
        user={user}
        onComplete={async () => {
          await refreshUser();
        }}
      />
    );
  }

  // Show with layout (navbar and sidebar)
  return (
    <DashboardLayout>
      <React.Suspense fallback={<PageLoader />}>
        <AdminManagementPage />
      </React.Suspense>
    </DashboardLayout>
  );
}

// Rekening page wrapper
function RekeningPageWrapper() {
  const { user, refreshUser } = useAuth();

  // Check if user needs onboarding - show full screen without layout
  if (user && (user.isPasswordSet === false || user.isSetCompleteAddress === false)) {
    return (
      <OnboardingWizard
        user={user}
        onComplete={async () => {
          await refreshUser();
        }}
      />
    );
  }

  // Show with layout (navbar and sidebar)
  return (
    <DashboardLayout>
      <React.Suspense fallback={<PageLoader />}>
        <RekeningPage />
      </React.Suspense>
    </DashboardLayout>
  );
}

// Transfer plan page wrapper
function TransferPlanPageWrapper() {
  const { user, refreshUser } = useAuth();

  // Check if user needs onboarding - show full screen without layout
  if (user && (user.isPasswordSet === false || user.isSetCompleteAddress === false)) {
    return (
      <OnboardingWizard
        user={user}
        onComplete={async () => {
          await refreshUser();
        }}
      />
    );
  }

  // Show with layout (navbar and sidebar)
  return (
    <DashboardLayout>
      <React.Suspense fallback={<PageLoader />}>
        <TransferPlanPage />
      </React.Suspense>
    </DashboardLayout>
  );
}

// Transfer plan list page wrapper
function TransferPlanListPageWrapper() {
  const { user, refreshUser } = useAuth();

  // Check if user needs onboarding - show full screen without layout
  if (user && (user.isPasswordSet === false || user.isSetCompleteAddress === false)) {
    return (
      <OnboardingWizard
        user={user}
        onComplete={async () => {
          await refreshUser();
        }}
      />
    );
  }

  // Show with layout (navbar and sidebar)
  return (
    <DashboardLayout>
      <React.Suspense fallback={<PageLoader />}>
        <TransferPlanListPage />
      </React.Suspense>
    </DashboardLayout>
  );
}

// Transfer plan location page wrapper
function TransferPlanLocationPageWrapper() {
  const { user, refreshUser } = useAuth();

  // Check if user needs onboarding - show full screen without layout
  if (user && (user.isPasswordSet === false || user.isSetCompleteAddress === false)) {
    return (
      <OnboardingWizard
        user={user}
        onComplete={async () => {
          await refreshUser();
        }}
      />
    );
  }

  // Show with layout (navbar and sidebar)
  return (
    <DashboardLayout>
      <React.Suspense fallback={<PageLoader />}>
        <TransferPlanLocationPage />
      </React.Suspense>
    </DashboardLayout>
  );
}

// Transfer plan detail page wrapper
function TransferPlanDetailPageWrapper() {
  const { user, refreshUser } = useAuth();

  // Check if user needs onboarding - show full screen without layout
  if (user && (user.isPasswordSet === false || user.isSetCompleteAddress === false)) {
    return (
      <OnboardingWizard
        user={user}
        onComplete={async () => {
          await refreshUser();
        }}
      />
    );
  }

  // Show with layout (navbar and sidebar)
  return (
    <DashboardLayout>
      <React.Suspense fallback={<PageLoader />}>
        <TransferPlanDetailPage />
      </React.Suspense>
    </DashboardLayout>
  );
}

// User detail by ID page wrapper
function UserDetailByIdPageWrapper() {
  const { user, refreshUser } = useAuth();

  // Check if user needs onboarding - show full screen without layout
  if (user && (user.isPasswordSet === false || user.isSetCompleteAddress === false)) {
    return (
      <OnboardingWizard
        user={user}
        onComplete={async () => {
          await refreshUser();
        }}
      />
    );
  }

  // Show with layout (navbar and sidebar)
  return (
    <DashboardLayout>
      <React.Suspense fallback={<PageLoader />}>
        <UserDetailByIdPage />
      </React.Suspense>
    </DashboardLayout>
  );
}

// Root redirect component
function RootRedirect() {
  const { isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();

  React.useEffect(() => {
    if (!isLoading) {
      if (isAuthenticated) {
        setLocation("/user");
      } else {
        setLocation("/login");
      }
    }
  }, [isAuthenticated, isLoading, setLocation]);

  return (
    <div className="h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"></div>
        <p className="mt-4 text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

export function App() {
  return (
    <Switch>
      <Route path="/" component={RootRedirect} />
      <Route path="/cari-callsign" component={CallsignLookupPage} />
      <PublicRoute path="/login" component={LoginPageWrapper} />
      <PublicRoute path="/auth/forgot-password" component={ForgotPasswordPageWrapper} />
      <PublicRoute path="/auth/reset-password" component={ResetPasswordPageWrapper} />
      <ProtectedRoute 
        path="/dashboard" 
        component={DashboardPageWrapper} 
        requiredAssignmentPrivileges={["view_reports"]}
      />
      <ProtectedRoute path="/user" component={UserDetailPageWrapper} />
      <ProtectedRoute path="/user/detail/:id" component={UserDetailByIdPageWrapper} />
      <ProtectedRoute 
        path="/historical-transaction" 
        component={HistoricalTransactionPageWrapper}
        requiredPrivileges={[
          "manage_transactions",
          "manage_transactions_plan",
          "view_reports",
        ]}
      />
      <Route path="/daftar/keanggotaan">
        {() => <Redirect href="/keanggotaan/daftar" />}
      </Route>
      <ProtectedRoute
        path="/keanggotaan/daftar"
        component={KeanggotaanPageWrapper}
        requiredAssignmentPrivileges={["manage_users"]}
      />
      <ProtectedRoute
        path="/keanggotaan/foto-ktp"
        component={FotoKtpPageWrapper}
        allowIf={(user) => user?.userLevel === 3}
      />
      <ProtectedRoute
        path="/keanggotaan/penangguhan"
        component={BlockPageWrapper}
        requiredAssignmentPrivileges={["manage_blocks"]}
      />
      <ProtectedRoute
        path="/keanggotaan/silent-key"
        component={SilentKeyPageWrapper}
        requiredAssignmentPrivileges={["manage_silentkey"]}
        requiredUserStage={3}
      />
      <ProtectedRoute
        path="/keanggotaan/nri-requests"
        component={NriRequestsPageWrapper}
        requiredAssignmentPrivileges={["manage_users"]}
      />
      {/* More specific routes must come before /logbook */}
      <ProtectedRoute
        path="/logbook/new"
        component={LogbookEntryFormPageWrapper}
        requiredPrivileges={["manage_users"]}
      />
      <ProtectedRoute 
        path="/logbook/:id/edit"
        component={LogbookEntryFormPageWrapper}
        requiredPrivileges={["manage_users"]}
      />
      <ProtectedRoute 
        path="/logbook" 
        component={LogbookPageWrapper}
        requiredPrivileges={["manage_users"]}
      />
      <ProtectedRoute 
        path="/qsl/sync" 
        component={QSLSyncPageWrapper}
        requiredPrivileges={["manage_users"]}
      />
      <ProtectedRoute path="/inbox" component={InboxRedirectWrapper} />
      <ProtectedRoute 
        path="/user-sync" 
        component={UserSyncPageWrapper}
        requiredPrivileges={["view_reports"]}
      />
      <ProtectedRoute 
        path="/management" 
        component={ManagementPageWrapper}
        requiredPrivileges={["manage_jabatan"]}
      />
      <ProtectedRoute 
        path="/admin-management" 
        component={AdminManagementPageWrapper}
        requiredPrivileges={["manage_admin_assignments"]}
      />
      <ProtectedRoute 
        path="/keuangan/rekening" 
        component={RekeningPageWrapper}
        requiredAssignmentPrivileges={["create_transaction_bank_account"]}
      />
      <ProtectedRoute 
        path="/keuangan/transfer-plan" 
        component={TransferPlanPageWrapper}
        requiredPrivileges={["create_transaction_plan", "manage_payments"]}
      />
      <ProtectedRoute 
        path="/keuangan/transfer-plans" 
        component={TransferPlanListPageWrapper}
        requiredAssignmentPrivileges={["manage_transfer_plans"]}
      />
      <ProtectedRoute 
        path="/keuangan/transfer-plans/:id/location" 
        component={TransferPlanLocationPageWrapper}
        requiredAssignmentPrivileges={["manage_transfer_plans"]}
      />
      <ProtectedRoute 
        path="/keuangan/transfer-plans/:id" 
        component={TransferPlanDetailPageWrapper}
        requiredAssignmentPrivileges={["manage_transfer_plans"]}
      />
      <Route>
        <RootRedirect />
      </Route>
    </Switch>
  );
}

export default App;
